import React from 'react';
import { AlertTriangle } from 'lucide-react';

/**
 * Error Boundary Component
 * 
 * Catches JavaScript errors anywhere in its child component tree,
 * logs those errors, and displays a fallback UI.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // You can log the error to an error reporting service
    console.error("Error caught by ErrorBoundary:", error, errorInfo);
  }

  render() {
    const { hasError, error } = this.state;
    const { fallback, children } = this.props;

    if (hasError) {
      // You can render any custom fallback UI
      if (fallback) {
        return fallback(error);
      }
      
      return (
        <div className="p-6 border border-error/20 bg-error/5 rounded-lg flex items-start gap-4">
          <div className="text-error mt-1">
            <AlertTriangle size={24} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-error mb-2">Something went wrong</h3>
            <p className="text-base-content/80">
              There was an error loading this component. Try refreshing the page.
            </p>
            {error && (
              <pre className="mt-4 p-3 bg-base-300 rounded text-sm overflow-auto max-h-48">
                {error.message}
              </pre>
            )}
            <button 
              className="btn btn-sm btn-error mt-4"
              onClick={() => this.setState({ hasError: false, error: null })}
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary; 